import React, { useMemo, useState } from "react";
import ModalModern from "../ModalModern";
import ModernPopupHeader from "../common/ModernPopupHeader";
import ModernPopupBody from "../common/ModernPopupBody";
import MultiSelect from "../../forms/MultiSelect";
import FieldLabel from "../../forms/FieldLabel";
import NotificationAlert from "../../alerts/notifications/NotificationAlert";
import { primarySpendingCategories } from "../../../../utils/constants/plaidCategoryTaxonomy";

function CreateRuleModal({
  content,
  onClose,
  onSave,
  metadata = [],
  apiError = "",
  isSubmitting = false,
  onClearApiError,
  ruleToEdit = null,
}) {
  const copy = content.subscriber_view.money_guardian.modals.create_edit_modal;

  const isEditMode = !!ruleToEdit;

  // Initialize state using ruleToEdit if it exists, matching the backend structure
  const [formData, setFormData] = useState({
    name: ruleToEdit ? ruleToEdit.name : "",
    description: ruleToEdit ? ruleToEdit.description : "",
    amount: ruleToEdit ? String(ruleToEdit.amount) : "",
    isEnable: ruleToEdit ? ruleToEdit.isEnable : true,

    // Extract the IDs if they are objects, otherwise fallback to standard strings
    institutions:
      ruleToEdit && ruleToEdit.institutions
        ? ruleToEdit.institutions.map((inst) =>
            String(inst.uuid || inst.id || inst),
          )
        : [],

    accounts:
      ruleToEdit && ruleToEdit.accounts
        ? ruleToEdit.accounts.map((acc) => String(acc.uuid || acc.id || acc))
        : [],

    categories:
      ruleToEdit && ruleToEdit.categories ? ruleToEdit.categories : [],
  });

  // 1. Map institution dropdown options safely using stringified values
  const institutionOptions = useMemo(() => {
    return (metadata || [])
      .map((inst) => {
        const idValue = inst.uuid || inst.id || inst.institution_id;
        if (!inst.institution_name || !idValue) return null;

        return {
          label: inst.institution_name,
          value: String(idValue),
        };
      })
      .filter(Boolean);
  }, [metadata]);

  // 2. Map account options safely with string-based inclusion filters
  const accountOptions = useMemo(() => {
    const safeMetadata = metadata || [];
    const fallbackAccount = copy.account_fallback_label;
    const fallbackInstitution = copy.institution_fallback_label;

    // Case A: No selection -> show ALL accounts across ALL connections
    if (!formData.institutions || formData.institutions.length === 0) {
      return safeMetadata.flatMap((inst) =>
        (inst.accounts || []).map((acc) => ({
          label: `${acc.name || fallbackAccount} (${
            inst.institution_name || fallbackInstitution
          })`,
          value: String(acc.uuid || acc.id || acc.account_id),
        })),
      );
    }

    // Case B: Filter accounts matching target selected institutions
    return safeMetadata
      .filter((inst) => {
        const instId = String(inst.uuid || inst.id || inst.institution_id);
        return formData.institutions.includes(instId);
      })
      .flatMap((inst) =>
        (inst.accounts || []).map((acc) => ({
          label: acc.name || fallbackAccount,
          value: String(acc.uuid || acc.id || acc.account_id),
        })),
      );
  }, [metadata, formData.institutions, copy]);

  const categoryOptions = primarySpendingCategories;

  // 3. String-safe handler to drop unaligned accounts upon changing institutions
  const handleInstitutionChange = (selectedInstUuids) => {
    setFormData((prev) => {
      const stringifiedSelectedUuids = (selectedInstUuids || []).map(String);

      const allowedAccountUuids = metadata
        .filter((inst) => {
          const instId = String(inst.uuid || inst.id || inst.institution_id);
          return stringifiedSelectedUuids.includes(instId);
        })
        .flatMap((inst) =>
          (inst.accounts || []).map((acc) =>
            String(acc.uuid || acc.id || acc.account_id),
          ),
        );

      const updatedAccounts = prev.accounts.filter((accUuid) =>
        allowedAccountUuids.includes(String(accUuid)),
      );

      return {
        ...prev,
        institutions: stringifiedSelectedUuids,
        accounts: updatedAccounts,
      };
    });
  };

  const handleMultiSelectChange = (fieldName, selectedValues) => {
    if (fieldName === "institutions") {
      handleInstitutionChange(selectedValues);
    } else {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: selectedValues,
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <ModalModern onClose={onClose}>
      <ModernPopupHeader onClose={onClose}>
        <div className="header-content-modern-row">
          <h3 className="transaction-popup-title">
            {isEditMode ? copy.title_edit : copy.title_create}
          </h3>
        </div>
      </ModernPopupHeader>

      <ModernPopupBody>
        <form
          onSubmit={handleSubmit}
          onFocus={() => apiError && onClearApiError?.()}
          className="mp-col-container"
        >
          {apiError && (
            <NotificationAlert
              alertType="error"
              alertTitle={
                isEditMode ? copy.alert_fail_edit : copy.alert_fail_create
              }
              alertDescription={apiError}
            />
          )}

          <div
            className="mp-col-container mobile-to-col"
            style={{ width: "100%", padding: "0" }}
          >
            <div
              className="mp-transacion-metadata"
              style={{ width: "100%", padding: "0" }}
            >
              {/* Row 1: Name and Trigger Amount Threshold */}
              <div
                className="mp-row-container mobile-to-col"
                style={{ gap: "20px", padding: "0", width: "100%" }}
              >
                <div className="form-field">
                  <p>{copy.field_name}</p>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    placeholder={copy.field_name_placeholder}
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-field">
                  <p>{copy.field_amount}</p>
                  <input
                    type="number"
                    name="amount"
                    className="form-input"
                    placeholder={copy.field_amount_placeholder}
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Row 2: Rule Purpose Description Textarea */}
              <div className="form-field">
                <p>{copy.field_description}</p>
                <textarea
                  name="description"
                  className="form-input"
                  placeholder={copy.field_description_placeholder}
                  value={formData.description}
                  onChange={handleChange}
                  style={{ height: "70px", resize: "none" }}
                />
              </div>

              {/* Row 3: Scoped MultiSelect Filters Block */}
              <div
                className="mp-col-container full-width ten-gap"
                style={{ marginTop: "10px" }}
              >
                <p
                  style={{
                    fontWeight: 500,
                    margin: "0 0 4px 0",
                    color: "#374151",
                  }}
                >
                  {copy.filters_heading}
                </p>

                <div className="form-field">
                  <FieldLabel label={copy.filter_institutions} />
                  <MultiSelect
                    options={institutionOptions}
                    value={formData.institutions}
                    onChange={(values) =>
                      handleMultiSelectChange("institutions", values)
                    }
                    placeholder={copy.filter_institutions_placeholder}
                  />
                </div>

                <div className="form-field">
                  <FieldLabel label={copy.filter_accounts} />
                  <MultiSelect
                    options={accountOptions}
                    value={formData.accounts}
                    onChange={(values) =>
                      handleMultiSelectChange("accounts", values)
                    }
                    placeholder={copy.filter_accounts_placeholder}
                  />
                </div>

                <div className="form-field">
                  <FieldLabel label={copy.filter_categories} />
                  <MultiSelect
                    options={categoryOptions}
                    value={formData.categories}
                    onChange={(values) =>
                      handleMultiSelectChange("categories", values)
                    }
                    placeholder={copy.filter_categories_placeholder}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions Footer Area */}
          <div
            className="mp-row-container align-center"
            style={{ marginTop: "24px", padding: "0" }}
          >
            <button
              type="submit"
              className="cta-button secondary"
              disabled={isSubmitting}
              style={{ width: "160px" }}
            >
              {isSubmitting
                ? copy.cta_submitting
                : isEditMode
                ? copy.cta_update
                : copy.cta_save}
            </button>
          </div>
        </form>
      </ModernPopupBody>
    </ModalModern>
  );
}

export default CreateRuleModal;
